/**
 * @file <bk-sort-menu>: the sort control of the facets toolbar (snippets/facets.liquid), drawn as a
 * listbox instead of the operating system's menu. On a desktop the native <select> opens the
 * system list, which carries none of the theme's type, colour or spacing; here a button opens a
 * designed list (a popover under the pill, a bottom sheet on phones).
 *
 * The native <select name="sort_by"> stays in the markup and stays the source of truth: choosing an
 * option sets its value and dispatches `change`, which assets/facets.js already turns into a
 * Section Rendering request and a new URL. Without JavaScript the select shows and the form submits.
 *
 * Keyboard (WAI-ARIA listbox): ArrowDown/ArrowUp/Enter/Space on the button open the list on the
 * selected option; ArrowDown/ArrowUp/Home/End move, typing letters jumps, Enter/Space choose,
 * Escape closes without a change, Tab closes and moves on. A pointer outside closes it. The toolbar
 * is re-rendered after a change, so focus is handed to the new button when it connects.
 */

const FOCUS_FLAG = 'bkSortFocus';

class BkSortMenu extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;

  #activeIndex = -1;

  #typed = '';

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #typedTimer;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.trigger?.addEventListener('click', () => (this.isOpen ? this.close() : this.open()), { signal });
    this.trigger?.addEventListener('keydown', (event) => this.#onTriggerKey(event), { signal });
    this.listbox?.addEventListener('keydown', (event) => this.#onListKey(event), { signal });
    this.listbox?.addEventListener(
      'click',
      (event) => {
        const option = event.target instanceof Element ? event.target.closest('[role="option"]') : null;
        if (option instanceof HTMLElement) this.#choose(option);
      },
      { signal },
    );
    this.listbox?.addEventListener(
      'pointermove',
      (event) => {
        const option = event.target instanceof Element ? event.target.closest('[role="option"]') : null;
        if (option instanceof HTMLElement) this.#setActive(this.options.indexOf(option), false);
      },
      { signal },
    );
    this.backdrop?.addEventListener('click', () => this.close(), { signal });
    document.addEventListener(
      'pointerdown',
      (event) => {
        if (this.isOpen && event.target instanceof Node && !this.contains(event.target)) this.close({ focus: false });
      },
      { signal },
    );

    const root = document.documentElement;
    if (root.dataset[FOCUS_FLAG]) {
      delete root.dataset[FOCUS_FLAG];
      this.trigger?.focus({ preventScroll: true });
    }
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
    clearTimeout(this.#typedTimer);
  }

  /** @returns {HTMLButtonElement | null} */
  get trigger() {
    return this.querySelector('[data-sort-trigger]');
  }

  /** @returns {HTMLElement | null} */
  get listbox() {
    return this.querySelector('[data-sort-listbox]');
  }

  /** @returns {HTMLElement | null} */
  get popover() {
    return this.querySelector('[data-sort-popover]');
  }

  /** @returns {HTMLElement | null} */
  get backdrop() {
    return this.querySelector('[data-sort-backdrop]');
  }

  /** @returns {HTMLSelectElement | null} */
  get select() {
    return this.querySelector('select[name="sort_by"]');
  }

  /** @returns {HTMLElement[]} */
  get options() {
    return [...this.querySelectorAll('[role="option"]')].filter((option) => option instanceof HTMLElement);
  }

  get isOpen() {
    return this.trigger?.getAttribute('aria-expanded') === 'true';
  }

  open() {
    const { trigger, popover, listbox } = this;
    if (!trigger || !popover || !listbox || this.isOpen) return;
    popover.hidden = false;
    if (this.backdrop) this.backdrop.hidden = false;
    trigger.setAttribute('aria-expanded', 'true');
    const selected = this.options.findIndex((option) => option.getAttribute('aria-selected') === 'true');
    this.#setActive(selected >= 0 ? selected : 0);
    listbox.focus({ preventScroll: true });
  }

  /** @param {{ focus?: boolean }} [options] */
  close({ focus = true } = {}) {
    const { trigger, popover, listbox } = this;
    if (!trigger || !popover || !this.isOpen) return;
    popover.hidden = true;
    if (this.backdrop) this.backdrop.hidden = true;
    trigger.setAttribute('aria-expanded', 'false');
    listbox?.removeAttribute('aria-activedescendant');
    for (const option of this.options) option.removeAttribute('data-active');
    if (focus) trigger.focus({ preventScroll: true });
  }

  /**
   * @param {number} index
   * @param {boolean} [scroll]
   */
  #setActive(index, scroll = true) {
    const options = this.options;
    if (!options.length || !this.listbox) return;
    this.#activeIndex = Math.max(0, Math.min(index, options.length - 1));
    options.forEach((option, i) => option.toggleAttribute('data-active', i === this.#activeIndex));
    const active = options[this.#activeIndex];
    this.listbox.setAttribute('aria-activedescendant', active.id);
    if (scroll) active.scrollIntoView({ block: 'nearest' });
  }

  /** @param {HTMLElement | undefined} option */
  #choose(option) {
    if (!option) return;
    const value = option.dataset.value ?? '';
    for (const candidate of this.options) candidate.setAttribute('aria-selected', String(candidate === option));
    const shown = this.querySelector('[data-sort-value]');
    if (shown) shown.textContent = option.textContent?.trim() ?? '';
    this.close();

    const select = this.select;
    if (!select || select.value === value) return;
    select.value = value;
    document.documentElement.dataset[FOCUS_FLAG] = '1';
    select.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /** @param {KeyboardEvent} event */
  #onTriggerKey(event) {
    if (!['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) return;
    event.preventDefault();
    this.open();
    if (event.key === 'ArrowUp') this.#setActive(this.options.length - 1);
  }

  /** @param {KeyboardEvent} event */
  #onListKey(event) {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.#setActive(this.#activeIndex + 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.#setActive(this.#activeIndex - 1);
        break;
      case 'Home':
        event.preventDefault();
        this.#setActive(0);
        break;
      case 'End':
        event.preventDefault();
        this.#setActive(this.options.length - 1);
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        this.#choose(this.options[this.#activeIndex]);
        break;
      case 'Escape':
        event.preventDefault();
        event.stopPropagation();
        this.close();
        break;
      case 'Tab':
        this.close({ focus: false });
        break;
      default:
        if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) this.#typeahead(event.key);
    }
  }

  /** @param {string} character */
  #typeahead(character) {
    clearTimeout(this.#typedTimer);
    this.#typed += character.toLocaleLowerCase();
    this.#typedTimer = setTimeout(() => {
      this.#typed = '';
    }, 600);
    const options = this.options;
    const start = this.#activeIndex + 1;
    const ordered = [...options.slice(start), ...options.slice(0, start)];
    const match = ordered.find((option) => (option.textContent ?? '').trim().toLocaleLowerCase().startsWith(this.#typed));
    if (match) this.#setActive(options.indexOf(match));
  }
}

if (!customElements.get('bk-sort-menu')) customElements.define('bk-sort-menu', BkSortMenu);
